import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    client_secret: '',
    account_name: ''
  });

  const storeId = localStorage.getItem('store_id');

  useEffect(() => {
    if (!storeId) {
      navigate('/');
      return;
    }

    checkSirvConfig();
  }, [storeId, navigate]);

  const checkSirvConfig = async () => {
    try {
      const response = await axios.get(`${API}/sirv/config`, {
        headers: {
          'X-Store-Id': storeId
        }
      });
      
      if (response.data.configured) {
        setConfigured(true);
        setFormData({
          client_id: response.data.client_id || '',
          client_secret: '',
          account_name: response.data.account_name || ''
        });
      }
    } catch (error) {
      console.error('Error checking Sirv config:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id || !formData.client_secret || !formData.account_name) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/sirv/config`, formData, {
        headers: {
          'X-Store-Id': storeId
        }
      });
      
      toast.success('Configuración de Sirv guardada correctamente');
      setConfigured(true);
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      console.error('Error saving Sirv config:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="settings-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="gap-2 mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-zinc-900">Configuración de Sirv</h1>
          <p className="text-zinc-600 mt-2">Configura tus credenciales de la API de Sirv</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Credenciales de API de Sirv</CardTitle>
            <CardDescription>
              Necesitas crear un cliente API en tu cuenta de Sirv para obtener estas credenciales.
              Ve a tu <a href="https://my.sirv.com/#/account/settings/api" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">panel de Sirv</a> para crearlas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="account_name" data-testid="account-name-label">Nombre de cuenta Sirv</Label>
                <Input
                  id="account_name"
                  type="text"
                  placeholder="tu-cuenta"
                  value={formData.account_name}
                  onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                  required
                  data-testid="account-name-input"
                />
                <p className="text-sm text-zinc-500">
                  El nombre de tu cuenta es el subdominio en tu URL de Sirv (ej: si tu URL es https://tu-cuenta.sirv.com, tu nombre de cuenta es "tu-cuenta")
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_id" data-testid="client-id-label">Client ID</Label>
                <Input
                  id="client_id"
                  type="text"
                  placeholder="Tu Client ID de Sirv"
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  required
                  data-testid="client-id-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_secret" data-testid="client-secret-label">Client Secret</Label>
                <Input
                  id="client_secret"
                  type="password"
                  placeholder="Tu Client Secret de Sirv"
                  value={formData.client_secret}
                  onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                  required
                  data-testid="client-secret-input"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">¿Cómo obtener las credenciales?</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  <li>Ve a <a href="https://my.sirv.com/#/account/settings/api" target="_blank" rel="noopener noreferrer" className="underline">Configuración de API de Sirv</a></li>
                  <li>Haz clic en "Create API client"</li>
                  <li>Dale un nombre descriptivo (ej: "Tiendanube App")</li>
                  <li>Copia el Client ID y Client Secret</li>
                  <li>Pégalos en los campos de arriba</li>
                </ol>
              </div>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="gap-2 flex-1"
                  data-testid="save-config-button"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Guardando...
                    </>
                  ) : configured ? (
                    <>
                      <Check className="w-4 h-4" />
                      Actualizar Configuración
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Guardar Configuración
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}